import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box, CircularProgress } from '@mui/material';

import { AppContainer, MainContentContainer, BodyContainer } from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { useIdentityAuth } from '../../auth';
import SearchIcon from '@mui/icons-material/Search';

import { sa, Card, DataTable, PillButton, SectionHeaderRow } from '../../components/superAdminUi';
import StatusFilterMenu from '../../components/userManagement/StatusFilterMenu';
import {
  ACTION_FILTER_OPTIONS,
  TARGET_TYPE_OPTIONS,
  formatAuditTime,
  readAuditPage,
  targetSummary,
} from '../../components/superAdminUi/auditFields';
import { getAuditLog } from '../../services/api/identityUserService';

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { search: '', action: '', targetType: '', from: '', to: '' };

// The native date inputs surface a day; the endpoint filters on ISO-UTC instants, so a chosen
// "From" opens the day and a chosen "To" closes it — inclusive of the whole selected date.
const toIsoStart = (date) => (date ? `${date}T00:00:00Z` : '');
const toIsoEnd = (date) => (date ? `${date}T23:59:59Z` : '');

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  font-family: ${sa.font.family};
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

// Free-text search replaces the old actor dropdown: an operator looks for a person or a target by
// name, not by GUID, and the dropdown could only ever list super admins — never a target.
const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};
  background: ${sa.color.white};
  padding: 6px 12px;

  svg {
    font-size: 18px;
    color: ${sa.color.textGray};
  }

  input {
    border: none;
    outline: none;
    background: transparent;
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    min-width: 200px;
  }
`;

const DateField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${sa.color.textGray};
  font-family: ${sa.font.family};

  input {
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    background: ${sa.color.white};
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    padding: 8px 10px;

    &:focus {
      outline: none;
      border-color: ${sa.color.teal};
    }
  }
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${sa.color.textGray};
  font-family: ${sa.font.family};
`;

const PageButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DetailCell = styled.span`
  white-space: normal;
  word-break: break-word;
`;

export default function AuditLog() {
  const { loading: authLoading, hasRole } = useIdentityAuth();
  const isSuperUser = !authLoading && hasRole('SuperUser');

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ entries: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Changing any filter drops the operator back to the first page so they never land on an
  // out-of-range page of the newly-filtered set.
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setPage(1);
  }, []);

  // Debounced so typing doesn't fire a request per keystroke; resets to page 1 via updateFilter.
  useEffect(() => {
    const handle = setTimeout(() => updateFilter('search', searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput, updateFilter]);

  useEffect(() => {
    if (!isSuperUser) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    getAuditLog({
      search: filters.search,
      action: filters.action,
      targetType: filters.targetType,
      from: toIsoStart(filters.from),
      to: toIsoEnd(filters.to),
      page,
      pageSize: PAGE_SIZE,
    })
      .then((body) => {
        if (!active) return;
        setData(readAuditPage(body, page, PAGE_SIZE));
      })
      .catch((e) => {
        if (!active) return;
        setError(e?.message || 'Failed to load the audit log.');
        setData({ entries: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isSuperUser, filters, page]);

  const columns = [
    {
      key: 'when',
      label: 'When',
      width: '18%',
      render: (row) => formatAuditTime(row.occurredAt),
    },
    {
      key: 'actor',
      label: 'Actor',
      width: '22%',
      render: (row) => row.actorEmail,
    },
    {
      key: 'action',
      label: 'Action',
      width: '16%',
      render: (row) => row.actionLabel,
    },
    {
      key: 'target',
      label: 'Target',
      width: '20%',
      render: (row) => targetSummary(row),
    },
    {
      key: 'details',
      label: 'Details',
      render: (row) => <DetailCell>{row.summary}</DetailCell>,
      cellStyle: { whiteSpace: 'normal' },
    },
  ];

  const renderPage = (content) => (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>{content}</MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );

  if (!authLoading && !isSuperUser) {
    return renderPage(
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card>
          <SectionHeaderRow title='Audit Log' size='lg' />
          <p style={{ marginTop: 12, color: sa.color.textGray, fontFamily: sa.font.family, fontSize: 14 }}>
            You need the Super User role to access this page.
          </p>
        </Card>
      </Box>,
    );
  }

  const { entries, totalCount, totalPages } = data;
  const currentPage = data.page || 1;
  const from = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, totalCount);

  const emptyNode = loading ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: sa.color.textGray }}>
      <CircularProgress size={18} sx={{ color: sa.color.teal }} />
      Loading audit log…
    </span>
  ) : error ? (
    error
  ) : (
    'No audit activity for these filters'
  );

  return renderPage(
    <Box>
      <Section>
        <SectionHeaderRow title='Audit Log' subtitle='Every admin action and sign-in, newest first' size='lg'>
          <Filters>
            <SearchBox>
              <SearchIcon />
              <input
                type='search'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder='Search actor or target…'
                aria-label='Search the audit log'
                data-testid='audit-search'
              />
            </SearchBox>
            <StatusFilterMenu
              value={filters.action}
              options={ACTION_FILTER_OPTIONS}
              onChange={(v) => updateFilter('action', v)}
            />
            <StatusFilterMenu
              value={filters.targetType}
              options={TARGET_TYPE_OPTIONS}
              onChange={(v) => updateFilter('targetType', v)}
            />
            <DateField>
              From
              <input
                type='date'
                value={filters.from}
                max={filters.to || undefined}
                onChange={(e) => updateFilter('from', e.target.value)}
                aria-label='From date'
              />
            </DateField>
            <DateField>
              To
              <input
                type='date'
                value={filters.to}
                min={filters.from || undefined}
                onChange={(e) => updateFilter('to', e.target.value)}
                aria-label='To date'
              />
            </DateField>
            <PillButton type='button' $variant='outline' onClick={clearFilters}>
              Clear filters
            </PillButton>
          </Filters>
        </SectionHeaderRow>

        <DataTable
          columns={columns}
          rows={loading ? [] : entries}
          getRowKey={(row) => row.id}
          empty={emptyNode}
        />

        <Pager>
          <span>
            Showing {from}–{to} of {totalCount}
          </span>
          <PageButtons>
            <PillButton
              type='button'
              $variant='outline'
              disabled={currentPage <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </PillButton>
            <span>
              Page {currentPage} of {Math.max(1, totalPages)}
            </span>
            <PillButton
              type='button'
              $variant='outline'
              disabled={currentPage >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </PillButton>
          </PageButtons>
        </Pager>
      </Section>
    </Box>,
  );
}
